import { useState, useEffect } from "react";
import { PurchaseOrder, POItem, SKU, Vendor, getVendorPriceComparison, getBestVendorForSKU, getCostSavingsAnalysis, getPriceTrend, getHistoricalPriceData } from "@/data/mockData";
import { useProcurement } from "@/context/ProcurementContext";
import { formatCurrency } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ShoppingCart, 
  Building2, 
  Package,
  Calendar,
  MapPin,
  FileText,
  Check,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Search,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  DollarSign,
  Clock,
  Star,
  CheckCircle,
  ArrowUpDown,
  History,
  Printer,
  Mail,
  MessageCircle
} from "lucide-react";
import { toast } from "sonner";

interface POCreationWizardProps {
  onSave: (poData: Partial<PurchaseOrder>) => Promise<boolean>;
  onCancel: () => void;
}

interface WizardStep {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

const steps: WizardStep[] = [
  { id: 'items', title: 'Add Items', description: 'Select products', icon: Package },
  { id: 'vendor', title: 'Select Vendor', description: 'Choose supplier', icon: Building2 },
  { id: 'details', title: 'Order Details', description: 'Delivery & notes', icon: FileText },
  { id: 'review', title: 'Review', description: 'Confirm order', icon: Check }
];

export const POCreationWizard = ({ onSave, onCancel }: POCreationWizardProps) => {
  const { state, actions } = useProcurement();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPostCreationActions, setShowPostCreationActions] = useState(false);
  const [createdPO, setCreatedPO] = useState<Partial<PurchaseOrder> | null>(null);

  // Load data when wizard opens
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load vendors and SKUs if not already loaded
        if (state.vendors.length === 0) {
          await actions.fetchVendors();
        }
        if (state.skus.length === 0) {
          await actions.fetchSKUs();
        }
      } catch (error) {
        console.error('Failed to load data for PO creation:', error);
      }
    };

    loadData();
  }, []);

  // Form data
  const [items, setItems] = useState<POItem[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null);
  const [deliveryDetails, setDeliveryDetails] = useState({
    expectedDate: "",
    address: "123 Company Street, City, State 12345",
    instructions: ""
  });
  const [notes, setNotes] = useState("");

  // Item search
  const [itemSearch, setItemSearch] = useState("");
  const [showItemSearch, setShowItemSearch] = useState(false);
  const [selectedItemForVendor, setSelectedItemForVendor] = useState<number | null>(null);
  
  // Vendor search
  const [vendorSearch, setVendorSearch] = useState("");

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Base prices for comparison (using first vendor or cheapest vendor)
  const [baseTotalAmount, setBaseTotalAmount] = useState<number | null>(null);
  
  // Price history modal state
  const [showPriceHistory, setShowPriceHistory] = useState(false);
  const [selectedItemForHistory, setSelectedItemForHistory] = useState<number | null>(null);
  

  const selectedVendor = selectedVendorId ? state.vendors.find(v => v.id === selectedVendorId) : null;
  
  // For items step - show all SKUs with defensive checks
  const availableSKUs = state.skus || [];
  
  // For vendor step - show SKUs linked to selected vendor
  const vendorLinkedSKUs = selectedVendor 
    ? (state.skus || []).filter(sku => (sku.linkedVendors || []).some(link => link.vendorId === selectedVendorId))
    : [];

  const filteredSKUs = availableSKUs.filter(sku => 
    sku.name?.toLowerCase().includes(itemSearch.toLowerCase()) ||
    sku.skuCode?.toLowerCase().includes(itemSearch.toLowerCase()) ||
    sku.category?.toLowerCase().includes(itemSearch.toLowerCase()) ||
    sku.description?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  // Calculate totals with defensive programming
  const totalAmount = items.reduce((sum, item) => {
    const itemTotal = item.totalPrice || 0;
    return sum + (isNaN(itemTotal) ? 0 : itemTotal);
  }, 0);

  // Validate current step
  const validateStep = (stepIndex: number) => {
    const newErrors: Record<string, string> = {};

    switch (stepIndex) {
      case 0: // Items selection
        if (items.length === 0) {
          newErrors.items = "Please add at least one item";
        }
        items.forEach((item, index) => {
          if (item.quantity <= 0) {
            newErrors[`item_${index}_quantity`] = "Quantity must be greater than 0";
          }
          if (item.unitPrice <= 0) {
            newErrors[`item_${index}_unitPrice`] = "Unit price must be greater than 0";
          }
        });
        break;
      case 1: // Vendor selection
        if (!selectedVendorId) {
          newErrors.vendor = "Please select a vendor";
        }
        // Allow all vendors to supply all items (no restrictions)
        break;
      case 2: // Details
        if (!deliveryDetails.expectedDate) {
          newErrors.expectedDate = "Please set expected delivery date";
        }
        if (!deliveryDetails.address.trim()) {
          newErrors.address = "Delivery address is required";
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Add item to PO
  const addItem = (sku: SKU) => {
    const existingItemIndex = items.findIndex(item => item.skuId === sku.id);
    
    if (existingItemIndex >= 0) {
      // Update existing item quantity
      const updatedItems = [...items];
      updatedItems[existingItemIndex].quantity += 1;
      const quantity = updatedItems[existingItemIndex].quantity;
      const unitPrice = updatedItems[existingItemIndex].unitPrice;
      const calculatedTotal = quantity * unitPrice;
      updatedItems[existingItemIndex].totalPrice = isNaN(calculatedTotal) ? 0 : calculatedTotal;
      setItems(updatedItems);
    } else {
      // Get initial price (cheapest vendor or first vendor if available)
      let initialPrice = 0;
      if (sku.linkedVendors && sku.linkedVendors.length > 0) {
        // Filter out zero prices and get the minimum valid price
        const validPrices = sku.linkedVendors
          .map(link => link.defaultPrice)
          .filter(price => price > 0);
        
        if (validPrices.length > 0) {
          initialPrice = Math.min(...validPrices);
        } else {
          // If all prices are zero, use the first non-zero price from price history
          const latestPrice = sku.priceHistory
            .filter(ph => ph.price > 0)
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
          
          if (latestPrice) {
            initialPrice = latestPrice.price;
          } else {
            // Fallback: use a default price based on SKU category
            const defaultPrices = {
              'Electronics': 1.00,
              'Electrical': 5.00,
              'Power': 10.00,
              'Control': 15.00,
              'Automation': 25.00
            };
            initialPrice = defaultPrices[sku.category as keyof typeof defaultPrices] || 1.00;
          }
        }
      } else {
        // No linked vendors, try to get price from price history
        const latestPrice = sku.priceHistory
          .filter(ph => ph.price > 0)
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
        
        if (latestPrice) {
          initialPrice = latestPrice.price;
        } else {
          // Fallback: use a default price based on SKU category
          const defaultPrices = {
            'Electronics': 1.00,
            'Electrical': 5.00,
            'Power': 10.00,
            'Control': 15.00,
            'Automation': 25.00
          };
          initialPrice = defaultPrices[sku.category as keyof typeof defaultPrices] || 1.00;
        }
      }
      
      // Add new item with initial pricing
      const calculatedTotal = 1 * initialPrice;
      
      // Debug logging
      console.log('Adding new item to PO:', {
        skuId: sku.id,
        skuName: sku.name,
        initialPrice,
        calculatedTotal,
        isValidTotal: !isNaN(calculatedTotal),
        linkedVendors: sku.linkedVendors?.length || 0,
        priceHistory: sku.priceHistory?.length || 0
      });
      
      const newItem: POItem = {
        id: Date.now(),
        skuId: sku.id,
        quantity: 1,
        unitPrice: initialPrice,
        totalPrice: isNaN(calculatedTotal) ? 0 : calculatedTotal,
        receivedQuantity: 0,
        acceptedQuantity: 0,
        rejectedQuantity: 0
      };
      
      setItems([...items, newItem]);
    }
    
    // Reset baseline for accurate comparison
    setBaseTotalAmount(null);
    setShowItemSearch(false);
    setItemSearch("");
  };

  // Update item
  const updateItem = (index: number, field: keyof POItem, value: any) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = updatedItems[index].quantity || 0;
      const unitPrice = updatedItems[index].unitPrice || 0;
      const calculatedTotal = quantity * unitPrice;
      updatedItems[index].totalPrice = isNaN(calculatedTotal) ? 0 : calculatedTotal;
      // Reset baseline when quantities change for accurate comparison
      setBaseTotalAmount(null);
    }
    
    setItems(updatedItems);
  };

  // Calculate baseline total for comparison (cheapest option)
  const calculateBaselineTotal = () => {
    if (items.length === 0) return 0;
    
    return items.reduce((total, item) => {
      const sku = (state.skus || []).find(s => s.id === item.skuId);
      if (!sku || !sku.linkedVendors || sku.linkedVendors.length === 0) return total;
      
      // Find cheapest vendor for this item
      const cheapestPrice = Math.min(...sku.linkedVendors.map(link => link.defaultPrice));
      return total + (item.quantity * cheapestPrice);
    }, 0);
  };

  // Update item prices when vendor is selected
  const updateItemPricesForVendor = (vendorId: number) => {
    // Set baseline if not set
    if (baseTotalAmount === null) {
      setBaseTotalAmount(calculateBaselineTotal());
    }
    
    const updatedItems = items.map(item => {
      const sku = (state.skus || []).find(s => s.id === item.skuId);
      const vendorLink = sku?.linkedVendors?.find(link => link.vendorId === vendorId);
      
      if (vendorLink) {
        const quantity = item.quantity || 0;
        const unitPrice = vendorLink.defaultPrice || 0;
        const calculatedTotal = quantity * unitPrice;
        return {
          ...item,
          unitPrice: unitPrice,
          totalPrice: isNaN(calculatedTotal) ? 0 : calculatedTotal
        };
      }
      
      return item;
    });
    
    setItems(updatedItems);
  };

  // Remove item
  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
    // Reset baseline for accurate comparison
    setBaseTotalAmount(null);
  };

  // Navigate steps
  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  // Submit PO
  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);

    const poData: Partial<PurchaseOrder> = {
      vendorId: selectedVendorId!,
      items,
      totalAmount,
      status: 'CREATED',
      deliveryDetails,
      notes,
      createdBy: state.currentUser?.id || 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      needsRevision: false,
      approvalHistory: [{
        id: 1,
        action: 'created',
        performedBy: state.currentUser?.id || 1,
        performedAt: new Date().toISOString(),
        remarks: 'Initial PO creation'
      }]
    };

    const success = await onSave(poData);
    setIsSubmitting(false);

    if (success) {
      toast.success("Purchase Order created successfully");
      // Store the created PO and show post-creation actions
      setCreatedPO(poData);
      setShowPostCreationActions(true);
    }
  };

  // Post-creation actions
  const handlePrintPO = () => {
    // Mock print functionality
    toast.info("Print functionality - PO would be printed");
    console.log("Printing PO:", createdPO);
  };

  const handleEmailPO = () => {
    // Mock email functionality
    toast.info("Email functionality - PO would be sent to vendor");
    console.log("Emailing PO to vendor:", createdPO);
  };

  const handleWhatsAppPO = () => {
    // Mock WhatsApp functionality
    toast.info("WhatsApp functionality - PO would be shared via WhatsApp");
    console.log("Sharing PO via WhatsApp:", createdPO);
  };

  const handleSendForApproval = () => {
    // Navigate to approval page
    if (createdPO && createdPO.id) {
      window.location.href = `/purchase-orders/approve/${createdPO.id}`;
    }
    toast.success("PO sent for approval");
    handleClosePostCreationActions();
  };

  const handleClosePostCreationActions = () => {
    setShowPostCreationActions(false);
    setCreatedPO(null);
    onCancel(); // Close the wizard
  };

  const getSKUName = (skuId: number) => {
    const sku = (state.skus || []).find(s => s.id === skuId);
    return sku ? sku.name : `SKU ${skuId}`;
  };

  const getSKUCode = (skuId: number) => {
    const sku = (state.skus || []).find(s => s.id === skuId);
    return sku ? sku.skuCode : `SKU-${skuId}`;
  };

  // Get recent transaction details for an item
  const getRecentTransactionDetails = (skuId: number) => {
    // Find recent purchase orders that contain this SKU
    const recentPOs = (state.purchaseOrders || [])
      .filter(po => (po.items || []).some(item => item.skuId === skuId))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 3); // Get last 3 transactions

    return recentPOs.map(po => {
      const item = (po.items || []).find(item => item.skuId === skuId);
      const vendor = (state.vendors || []).find(v => v.id === po.vendorId);
      return {
        poNumber: po.poNumber,
        vendorName: vendor?.name || 'Unknown Vendor',
        unitPrice: item?.unitPrice || 0,
        quantity: item?.quantity || 0,
        totalPrice: item?.totalPrice || 0,
        date: po.createdAt,
        status: po.status
      };
    });
  };

  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        {/* Sticky Header */}
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Create Purchase Order
          </DialogTitle>
          <DialogDescription>
            Follow the steps to create a new purchase order
          </DialogDescription>
        </DialogHeader>

        {/* Sticky Step Flow */}
        <div className="flex-shrink-0 space-y-4">
          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center gap-2 ${
                    isActive ? 'text-primary' : isCompleted ? 'text-green-600' : 'text-muted-foreground'
                  }`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isActive ? 'bg-primary text-primary-foreground' :
                      isCompleted ? 'bg-green-600 text-white' :
                      'bg-muted'
                    }`}>
                      {isCompleted ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                    </div>
                    <div className="hidden sm:block">
                      <p className="text-sm font-medium">{step.title}</p>
                      <p className="text-xs">{step.description}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="w-8 h-px bg-border mx-4" />
                  )}
                </div>
              );
            })}
          </div>

          <Progress value={(currentStep + 1) / steps.length * 100} className="h-2" />
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto space-y-6 px-1 min-h-0">
          {/* Step Content */}
          <Card>
            <CardContent className="p-6">
              {/* Step 1: Items Selection */}
              {currentStep === 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">Add Items</h3>
                      <p className="text-sm text-muted-foreground">
                        Select products to include in this purchase order
                      </p>
                    </div>
                    {items.length === 0 && (
                      <Button onClick={() => setShowItemSearch(true)} size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Item
                      </Button>
                    )}
                  </div>

                  {errors.items && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 rounded-lg">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <p className="text-sm text-red-500">{errors.items}</p>
                    </div>
                  )}

                  {/* Item Search Modal */}
                  {showItemSearch && (
                    <Card className="border-primary">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Package className="h-4 w-4" />
                          Select Items
                        </CardTitle>
                        <CardDescription>
                          Search and select products to add to your purchase order
                        </CardDescription>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search by name, SKU code, or category..."
                            value={itemSearch}
                            onChange={(e) => setItemSearch(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                      </CardHeader>
                      <CardContent className="max-h-80 overflow-y-auto">
                    {filteredSKUs.length > 0 ? (
                      <div className="space-y-2">
                        {filteredSKUs.map(sku => {
                          const isAlreadyAdded = items.some(item => item.skuId === sku.id);
                          
                          return (
                            <div 
                              key={sku.id} 
                              className={`flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors ${
                                isAlreadyAdded ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800' : ''
                              }`}
                              onClick={() => !isAlreadyAdded && addItem(sku)}
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                    <Package className="h-5 w-5 text-primary" />
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-sm">{sku.name}</h4>
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline" className="text-xs">
                                        {sku.skuCode}
                                      </Badge>
                                      <Badge variant="secondary" className="text-xs">
                                        {sku.category}
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="text-muted-foreground">Current Stock</p>
                                    <p className="font-medium">{sku.currentStock} {sku.unit}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Unit</p>
                                    <p className="font-medium">{sku.unit}</p>
                                  </div>
                                </div>

                                {/* Previous Transaction Details - Highlighted */}
                                {(() => {
                                  const recentTransactions = getRecentTransactionDetails(sku.id);
                                  const mostRecent = recentTransactions[0];
                                  return mostRecent ? (
                                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                      <div className="flex items-center gap-2 mb-2">
                                        <History className="h-4 w-4 text-blue-600" />
                                        <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">Most Recent Purchase</p>
                                      </div>
                                      <div className="grid grid-cols-2 gap-3 text-xs">
                                        <div>
                                          <p className="text-blue-600 dark:text-blue-400">PO Number</p>
                                          <p className="font-medium text-blue-800 dark:text-blue-200">{mostRecent.poNumber}</p>
                                        </div>
                                        <div>
                                          <p className="text-blue-600 dark:text-blue-400">Vendor</p>
                                          <p className="font-medium text-blue-800 dark:text-blue-200">{mostRecent.vendorName}</p>
                                        </div>
                                        <div>
                                          <p className="text-blue-600 dark:text-blue-400">Unit Price</p>
                                          <p className="font-medium text-blue-800 dark:text-blue-200">{formatCurrency(mostRecent.unitPrice)}</p>
                                        </div>
                                        <div>
                                          <p className="text-blue-600 dark:text-blue-400">Quantity</p>
                                          <p className="font-medium text-blue-800 dark:text-blue-200">{mostRecent.quantity}</p>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700 rounded-lg">
                                      <p className="text-xs text-gray-600 dark:text-gray-400">No previous purchase history</p>
                                    </div>
                                  );
                                })()}

                                {/* Show vendor information */}
                                {sku.linkedVendors && sku.linkedVendors.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-xs text-muted-foreground mb-1">Available from {sku.linkedVendors.length} vendor(s)</p>
                                    <div className="flex flex-wrap gap-1">
                                      {sku.linkedVendors.slice(0, 3).map(link => {
                                        const vendor = (state.vendors || []).find(v => v.id === link.vendorId);
                                        return (
                                          <Badge key={link.vendorId} variant="outline" className="text-xs">
                                            {vendor?.name} - {formatCurrency(link.defaultPrice)}
                                          </Badge>
                                        );
                                      })}
                                      {sku.linkedVendors.length > 3 && (
                                        <Badge variant="outline" className="text-xs">
                                          +{sku.linkedVendors.length - 3} more
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              <div className="text-right ml-4">
                                {isAlreadyAdded ? (
                                  <div className="mt-2 flex items-center gap-1 text-green-600">
                                    <CheckCircle className="h-4 w-4" />
                                    <span className="text-xs font-medium">Added</span>
                                  </div>
                                ) : (
                                  <Button 
                                    size="sm" 
                                    className="mt-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      addItem(sku);
                                    }}
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                        ) : (
                          <div className="text-center py-8 text-muted-foreground">
                            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                            <p className="font-medium">No items found</p>
                            <p className="text-sm">
                              {itemSearch 
                                ? "Try adjusting your search terms"
                                : "No products available from this vendor"
                              }
                            </p>
                          </div>
                        )}
                        
                        <div className="flex justify-between items-center mt-4 pt-4 border-t">
                          <div className="text-sm text-muted-foreground">
                            {filteredSKUs.length} items available
                          </div>
                          <Button variant="outline" onClick={() => setShowItemSearch(false)}>
                            Close
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Items List */}
                  {items.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold">Selected Items ({items.length})</h4>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setShowItemSearch(true)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add More Items
                        </Button>
                      </div>
                      
                      {items.map((item, index) => {
                        const sku = (state.skus || []).find(s => s.id === item.skuId);
                        const recentTransactions = getRecentTransactionDetails(item.skuId);
                        return (
                          <Card key={item.id} className="border-l-4 border-l-primary">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2">
                                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                                      <Package className="h-4 w-4 text-primary" />
                                    </div>
                                    <div>
                                      <h4 className="font-semibold text-sm">{getSKUName(item.skuId)}</h4>
                                      <div className="flex items-center gap-2">
                                        <Badge variant="outline" className="text-xs">
                                          {getSKUCode(item.skuId)}
                                        </Badge>
                                        {sku && (
                                          <Badge variant="secondary" className="text-xs">
                                            {sku.category}
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground mb-3">
                                    <div>
                                      <span>Unit: </span>
                                      <span className="font-medium">{sku?.unit || 'pcs'}</span>
                                    </div>
                                    <div>
                                      <span>Stock: </span>
                                      <span className="font-medium">{sku?.currentStock || 0}</span>
                                    </div>
                                  </div>

                                  {/* Most Recent Transaction */}
                                  {recentTransactions.length > 0 && (
                                    <div className="mb-3 p-2 bg-muted/50 rounded-lg">
                                      <div className="flex items-center justify-between text-xs">
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium">{recentTransactions[0].poNumber}</span>
                                          <span className="text-muted-foreground">- {recentTransactions[0].vendorName}</span>
                                          <span className="text-muted-foreground">({new Date(recentTransactions[0].date).toLocaleDateString()})</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium">{formatCurrency(recentTransactions[0].unitPrice)}</span>
                                          <span className="text-muted-foreground">× {recentTransactions[0].quantity}</span>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="flex items-center gap-4">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Quantity</Label>
                                    <Input
                                      type="number"
                                      value={item.quantity}
                                      onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)}
                                      className="h-8 w-auto min-w-[60px] max-w-[120px]"
                                      style={{ width: `${Math.max(60, (String(item.quantity).length * 12) + 24)}px` }}
                                      min="1"
                                    />
                                    {errors[`item_${index}_quantity`] && (
                                      <p className="text-xs text-red-500">{errors[`item_${index}_quantity`]}</p>
                                    )}
                                  </div>
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-1">
                                      <Label className={`text-xs ${item.unitPrice === 0 ? 'text-red-600 font-semibold' : ''}`}>
                                        Unit Price {item.unitPrice === 0 && '(Required)'}
                                      </Label>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-4 w-4 p-0 hover:bg-transparent"
                                        onClick={() => {
                                          setSelectedItemForHistory(item.skuId);
                                          setShowPriceHistory(true);
                                        }}
                                        title="View price history"
                                      >
                                        <History className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                      </Button>
                                    </div>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={item.unitPrice}
                                      onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                                      className={`h-8 w-auto min-w-[80px] max-w-[140px] ${item.unitPrice === 0 ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500' : ''}`}
                                      style={{ width: `${Math.max(80, (String(item.unitPrice).length * 10) + 24)}px` }}
                                      min="0"
                                      placeholder="0.00"
                                    />
                                    {item.unitPrice === 0 && (
                                      <p className="text-xs text-red-500">
                                        Please enter a valid unit price for this item
                                      </p>
                                    )}
                                    {errors[`item_${index}_unitPrice`] && (
                                      <p className="text-xs text-red-500">{errors[`item_${index}_unitPrice`]}</p>
                                    )}
                                  </div>
                                  <div className="text-right min-w-0">
                                    <p className="text-xs text-muted-foreground">Total</p>
                                    <p className="font-semibold text-lg break-words">{formatCurrency(item.totalPrice)}</p>
                                    <p className="text-xs text-muted-foreground break-words">
                                      {item.quantity} × {formatCurrency(item.unitPrice)}
                                    </p>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedItemForHistory(item.skuId);
                                      setShowPriceHistory(true);
                                    }}
                                    className="text-blue-500 hover:text-blue-700 border-blue-200 hover:border-blue-300 p-2"
                                    title="View detailed transaction history"
                                  >
                                    <History className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeItem(index)}
                                    className="text-red-500 hover:text-red-700"
                                    title="Remove item"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                      
                      <Card className="bg-primary/5 border-primary/20">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="font-semibold text-lg">Total Amount:</span>
                              <p className="text-sm text-muted-foreground">
                                {items.length} item{items.length !== 1 ? 's' : ''} selected
                              </p>
                            </div>
                            <span className="text-2xl font-bold text-primary">{formatCurrency(totalAmount)}</span>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Vendor Selection with Cost Comparison */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Select Vendor</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Compare vendors and choose the best supplier for this purchase order
                      </p>
                    </div>
                  </div>

                  {errors.vendor && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 rounded-lg">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <p className="text-sm text-red-500">{errors.vendor}</p>
                    </div>
                  )}

                  {items.length > 0 && (
                    <div className="space-y-4">
                      {/* Vendor Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search vendors by name or code..."
                          className="pl-10"
                          value={vendorSearch}
                          onChange={(e) => setVendorSearch(e.target.value)}
                        />
                      </div>

                      <div className="space-y-4">
                        <div className="grid gap-3">
                          {(state.vendors || [])
                            .filter(vendor => vendor.isActive)
                            .filter(vendor => 
                              vendor.name.toLowerCase().includes(vendorSearch.toLowerCase()) ||
                              vendor.vendorCode.toLowerCase().includes(vendorSearch.toLowerCase()) ||
                              (vendor.contactDetails?.email || '').toLowerCase().includes(vendorSearch.toLowerCase())
                            )
                            .map((vendor) => {
                            const isSelected = selectedVendorId === vendor.id;
                            
                            // Check if this vendor has recent purchases for any of the selected items
                            const hasRecentPurchases = items.some(item => {
                              const recentTransactions = getRecentTransactionDetails(item.skuId);
                              return recentTransactions.some(transaction => 
                                transaction.vendorName === vendor.name
                              );
                            });

                            return (
                              <div
                                key={vendor.id}
                                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                                  isSelected 
                                    ? 'border-primary bg-primary/5 ring-1 ring-primary' 
                                    : hasRecentPurchases
                                    ? 'border-green-200 bg-green-50/50 hover:border-green-300 hover:bg-green-50 ring-1 ring-green-200 shadow-sm'
                                    : 'hover:border-muted-foreground/50'
                                }`}
                                onClick={() => {
                                  setSelectedVendorId(vendor.id);
                                  updateItemPricesForVendor(vendor.id);
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                        hasRecentPurchases ? 'bg-green-100' : 'bg-primary/10'
                                      }`}>
                                        <Building2 className={`h-5 w-5 ${
                                          hasRecentPurchases ? 'text-green-600' : 'text-primary'
                                        }`} />
                                      </div>
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <h5 className="font-semibold text-lg">{vendor.name}</h5>
                                          {hasRecentPurchases && (
                                            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 border-green-200">
                                              Recent Supplier
                                            </Badge>
                                          )}
                                        </div>
                                        <p className="text-sm text-muted-foreground">{vendor.vendorCode}</p>
                                      </div>
                                    </div>
                                    
                                    <div className="text-sm text-muted-foreground">
                                      <p>{vendor.contactDetails?.email || 'N/A'}</p>
                                      <p>{vendor.contactDetails?.phone || 'N/A'}</p>
                                    </div>
                                  </div>
                                  
                                  <div className="ml-4">
                                    {isSelected && (
                                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                                        <Check className="h-4 w-4 text-white" />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>


                    </div>
                  )}

                </div>
              )}

              {/* Step 3: Details */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Order Details</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Set delivery details and additional information
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="expectedDate">Required by *</Label>
                      <Input
                        id="expectedDate"
                        type="date"
                        value={deliveryDetails.expectedDate}
                        onChange={(e) => setDeliveryDetails({ ...deliveryDetails, expectedDate: e.target.value })}
                        className={errors.expectedDate ? "border-red-500" : ""}
                      />
                      {errors.expectedDate && <p className="text-sm text-red-500">{errors.expectedDate}</p>}
                    </div>


                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="address">Delivery Address *</Label>
                      <Textarea
                        id="address"
                        value={deliveryDetails.address}
                        onChange={(e) => setDeliveryDetails({ ...deliveryDetails, address: e.target.value })}
                        className={errors.address ? "border-red-500" : ""}
                        rows={3}
                      />
                      {errors.address && <p className="text-sm text-red-500">{errors.address}</p>}
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="instructions">Delivery Instructions</Label>
                      <Textarea
                        id="instructions"
                        value={deliveryDetails.instructions}
                        onChange={(e) => setDeliveryDetails({ ...deliveryDetails, instructions: e.target.value })}
                        placeholder="Special delivery instructions..."
                        rows={2}
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Additional notes for this purchase order..."
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Review */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Review Purchase Order</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Please review all details before submitting
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Vendor Information</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <p><span className="font-medium">Name:</span> {selectedVendor?.name}</p>
                        <p><span className="font-medium">Code:</span> {selectedVendor?.vendorCode}</p>
                        <p><span className="font-medium">Email:</span> {selectedVendor?.contactDetails?.email || 'N/A'}</p>
                        <p><span className="font-medium">Phone:</span> {selectedVendor?.contactDetails?.phone || 'N/A'}</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Delivery Details</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <p><span className="font-medium">Required by:</span> {new Date(deliveryDetails.expectedDate).toLocaleDateString()}</p>
                        <p><span className="font-medium">Address:</span> {deliveryDetails.address}</p>
                        {deliveryDetails.instructions && (
                          <p><span className="font-medium">Instructions:</span> {deliveryDetails.instructions}</p>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Order Items</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {items.map((item) => {
                          const sku = (state.skus || []).find(s => s.id === item.skuId);
                          return (
                            <div key={item.id} className="flex justify-between items-center p-3 border rounded">
                              <div>
                                <p className="font-medium">{getSKUName(item.skuId)}</p>
                                <p className="text-sm text-muted-foreground">{getSKUCode(item.skuId)}</p>
                                {sku && (
                                  <p className="text-xs text-muted-foreground">
                                    Current Stock: {sku.currentStock} {sku.unit}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="font-semibold">{formatCurrency(item.totalPrice)}</p>
                                <p className="text-sm text-muted-foreground">
                                  {item.quantity} × {formatCurrency(item.unitPrice)}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                        
                        <Separator />
                        
                        <div className="flex justify-between items-center font-semibold text-lg">
                          <span>Total Amount:</span>
                          <span>{formatCurrency(totalAmount)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {notes && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Notes</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm">{notes}</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sticky Footer */}
        <div className="flex-shrink-0 border-t pt-4">
          <div className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={currentStep === 0 ? onCancel : prevStep}
              disabled={isSubmitting}
            >
              {currentStep === 0 ? (
                "Cancel"
              ) : (
                <>
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Previous
                </>
              )}
            </Button>
            
            <Button 
              onClick={currentStep === steps.length - 1 ? handleSubmit : nextStep}
              disabled={isSubmitting}
            >
              {currentStep === steps.length - 1 ? (
                isSubmitting ? "Creating..." : "Create Purchase Order"
              ) : (
                <>
                  Next
                  <ChevronRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Price History Modal */}
        {showPriceHistory && selectedItemForHistory && (
          <Dialog open={showPriceHistory} onOpenChange={(open) => {
            setShowPriceHistory(open);
            if (!open) setSelectedItemForHistory(null);
          }}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Price History
                </DialogTitle>
                <DialogDescription>
                  Historical purchase prices for {getSKUName(selectedItemForHistory)}
                </DialogDescription>
              </DialogHeader>

              {(() => {
                const sku = (state.skus || []).find(s => s.id === selectedItemForHistory);
                if (!sku) return <p>SKU not found</p>;

                const historicalData = getHistoricalPriceData(selectedItemForHistory);
                if (!historicalData || historicalData.priceData.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <History className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="font-medium text-muted-foreground">No price history available</p>
                      <p className="text-sm text-muted-foreground">This item hasn't been purchased before</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium">SKU Code</p>
                        <p className="text-lg">{sku.skuCode}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Current Stock</p>
                        <p className="text-lg">{sku.currentStock} {sku.unit}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-semibold">Recent Purchase History</h4>
                      {historicalData.priceData.slice(-10).reverse().map((entry) => {
                        const purchaseDate = new Date(entry.date);
                        const isRecent = (new Date().getTime() - purchaseDate.getTime()) < (30 * 24 * 60 * 60 * 1000); // 30 days
                        
                        return (
                          <div key={entry.id} className="flex items-center justify-between p-4 border rounded-lg">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-lg">{formatCurrency(entry.price)}</span>
                                {isRecent && (
                                  <Badge variant="secondary" className="text-xs">Recent</Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <div className="flex items-center gap-1">
                                  <Building2 className="h-3 w-3" />
                                  {entry.vendorName}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {purchaseDate.toLocaleDateString('en-IN', { 
                                    day: 'numeric', 
                                    month: 'short', 
                                    year: 'numeric' 
                                  })}
                                </div>
                              </div>
                              {entry.reason && (
                                <div className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                                  {entry.reason}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {historicalData.priceData.length > 10 && (
                      <div className="text-center text-sm text-muted-foreground">
                        Showing last 10 entries of {historicalData.priceData.length} total records
                      </div>
                    )}

                    <div className="flex justify-between items-center pt-4 border-t">
                      <div className="text-sm text-muted-foreground">
                        Price history from {new Date(historicalData.dateRange.start!).toLocaleDateString('en-IN')} to {new Date(historicalData.dateRange.end!).toLocaleDateString('en-IN')}
                      </div>
                      <Button onClick={() => {
                        setShowPriceHistory(false);
                        setSelectedItemForHistory(null);
                      }}>
                        Close
                      </Button>
                    </div>
                  </div>
                );
              })()}
            </DialogContent>
          </Dialog>
        )}

        {/* Post-Creation Actions Modal */}
        {showPostCreationActions && createdPO && (
          <Dialog open={showPostCreationActions} onOpenChange={setShowPostCreationActions}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  PO Created Successfully!
                </DialogTitle>
                <DialogDescription>
                  Purchase Order {createdPO.poNumber} has been created. What would you like to do next?
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-3">
                  <Button
                    onClick={handlePrintPO}
                    variant="outline"
                    className="flex items-center gap-2 h-12"
                  >
                    <Printer className="h-4 w-4" />
                    Print Purchase Order
                  </Button>
                  
                  <Button
                    onClick={handleEmailPO}
                    variant="outline"
                    className="flex items-center gap-2 h-12"
                  >
                    <Mail className="h-4 w-4" />
                    Send Email to Vendor
                  </Button>
                  
                  <Button
                    onClick={handleWhatsAppPO}
                    variant="outline"
                    className="flex items-center gap-2 h-12 bg-green-600 hover:bg-green-700 text-white border-green-600"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Share via WhatsApp
                  </Button>
                  
                  <Button
                    onClick={handleSendForApproval}
                    className="flex items-center gap-2 h-12 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Send for Approval
                  </Button>
                </div>
                
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    onClick={handleClosePostCreationActions}
                    variant="outline"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
};
